import type { JobStatus } from '@conversia/shared-types';

/**
 * El DÍA del técnico — lógica PURA para la ficha del tracking:
 * la ruta (puntos de ponche en orden) y, más abajo, su línea de tiempo.
 */

export interface PuntoRuta {
  lat: number;
  lng: number;
}

interface JobConPonche {
  arrivedLat: number | null;
  arrivedLng: number | null;
  arrivedAt: string | null;
}

/**
 * Ruta del día: los puntos donde el técnico PONCHÓ llegada, en orden
 * cronológico, terminando en su posición actual (si se conoce). Con menos de
 * 2 puntos no hay línea que dibujar (devuelve lo que haya; el mapa decide).
 */
export function rutaDelDia<T extends JobConPonche>(
  jobs: T[],
  posicionActual?: PuntoRuta | null,
): PuntoRuta[] {
  const puntos = jobs
    .filter(
      (j): j is T & { arrivedAt: string; arrivedLat: number; arrivedLng: number } =>
        j.arrivedAt != null && j.arrivedLat != null && j.arrivedLng != null,
    )
    .sort((a, b) => a.arrivedAt.localeCompare(b.arrivedAt))
    .map((j) => ({ lat: j.arrivedLat, lng: j.arrivedLng }));
  if (posicionActual) puntos.push(posicionActual);
  return puntos;
}

// ------------------- Línea de tiempo del DÍA del técnico -------------------

export type EstadoDia = 'completado' | 'en_curso' | 'pendiente' | 'no_completado' | 'cancelado';

interface JobDelDia extends JobConPonche {
  estado: JobStatus;
  scheduledAt: string | null;
}

export interface PasoDia<T> {
  job: T;
  estado: EstadoDia;
  /** Hora del paso: la de PONCHE si existe (real), si no la de la cita. */
  horaIso: string | null;
  /** true = hora de ponche real; false = hora de cita (estimada). */
  horaReal: boolean;
}

const ESTADO_DIA: Record<JobStatus, EstadoDia> = {
  ASIGNADO: 'pendiente',
  EN_SITIO: 'en_curso',
  // S-01: el técnico ya terminó su visita. Para SU día del técnico eso es
  // trabajo hecho — lo que falta (la validación comercial) es del panel, no de
  // él, y marcarlo "en curso" le dejaría un trabajo abierto que no puede cerrar.
  PENDIENTE_VALIDACION: 'completado',
  COMPLETADO: 'completado',
  NO_COMPLETADO: 'no_completado',
  CANCELADO: 'cancelado',
};

/**
 * La secuencia del día del técnico: sus trabajos ordenados por hora de ponche
 * (real) o de cita, con el estado de cada uno. Sin hora → al final.
 */
export function tecnicoDayTimeline<T extends JobDelDia>(jobs: T[]): PasoDia<T>[] {
  return [...jobs]
    .map((job) => ({
      job,
      estado: ESTADO_DIA[job.estado],
      horaIso: job.arrivedAt ?? job.scheduledAt,
      horaReal: job.arrivedAt != null,
    }))
    .sort((a, b) => (a.horaIso ?? '9999').localeCompare(b.horaIso ?? '9999'));
}
